<template>
  <div class="mx-auto  p-4 select-none" @click="onResponse" @touchstart.prevent="onResponse">
        <!-- Úvod a štart -->
    <div v-if="phase==='ready'">
        <p>Krátky test udržania pozornosti v monotónnej situácii. Na ciferníku sa ručička posúva v pravidelnom rytme; občas nastane dvojskok. Úlohou je čo najrýchlejšie kliknúť hneď po zaznamenaní dvojskoku. Test trvá približne 120 sekúnd.
            Test dopĺňa ostatné údaje o výkonovú mieru pozornosti. Hlavné ukazovatele sú: hit rate (podiel správnych zásahov), false alarms/min (falošné poplachy za minútu) a medián reakčného času na ciele. Tieto metriky umožnia posúdiť odolnosť voči interferencii a rýchlosť detekcie pri opakovanom, málo podnetnom vstupe.</p>
        <p>Počas testu nie je možné prerušenie; ak stránku opustíte, zaznamená sa dovtedajšie trvanie a priebežné výsledky. </p>
        <p><b>Sledujte ručičku. Občas preskočí o dve pozície (dvojskok). Kliknite do plochy aplikácie hneď, ako dvojskok zaregistrujete.</b></p>
        
    </div>
    <div v-if="phase==='ready'" class="text-center mb-4" >
      <div class="text-left mb-3">
        <label class="inline-flex items-start gap-2">
          <input type="checkbox" v-model="agreement" :true-value="1" :false-value="0" />
          <span class="text-xs opacity-80">
            Súhlasím s anonymným spracovaním mojich odpovedí na vedecké účely.
          </span>
        </label>
      </div>
      <v-btn color="primary" @click.stop="start" :disabled="agreement !== 1">Spustiť</v-btn>
    </div>

    <!-- Priebeh -->
    <div v-if="phase!=='ready'" class="mb-3">
      <div class="flex justify-between text-xs mt-1">
        <span>Čas: {{ elapsedSec }} s</span>
        <span>Ciele: {{ hits.length }} / {{ targetCount }}</span>
      </div>
    </div>

    <!-- Hodiny (SVG) -->
    <div class="flex items-center justify-center my-4">
      <svg :width="clockSize" :height="clockSize" viewBox="0 0 200 200">
        <!-- ciferník -->
        <circle cx="100" cy="100" r="90" fill="#fafafa" stroke="#ddd" stroke-width="2"/>
        <!-- značky -->
        <g v-for="i in 60" :key="'tick-'+i" :transform="'rotate(' + (i*6) + ' 100 100)'">
          <line x1="100" y1="14" x2="100" :y2="(i%5===0)?24:18" :stroke="(i%5===0)?'#888':'#bbb'" stroke-width="1"/>
        </g>
        <!-- ručička (farebná spätná väzba) -->
        <g :transform="'rotate(' + pointerAngle + ' 100 100)'">
          <line x1="100" y1="100" x2="100" y2="26" :stroke="handColor" stroke-width="3" stroke-linecap="round"/>
          <circle cx="100" cy="100" r="4" :fill="handColor"/>
        </g>
      </svg>
    </div>

    <!-- Výsledky -->

    <div v-if="phase==='finished'" class="mt-4">
     <v-card outlined class="pa-3 mt-4">         
      <h3 class="font-semibold mb-2">Výsledky</h3>
      <ul class="text-sm mb-3">
        <li><strong>Trvanie (s):</strong> {{ durationSec }}</li>
        <li><strong>Hit rate:</strong> {{ hitRate.toFixed(2) }}</li>
        <li><strong>False alarms / min:</strong> {{ falseAlarmsPerMin.toFixed(2) }}</li>
        <li v-if="medianRT!==null"><strong>Medián RT (ms):</strong> {{ Math.round(medianRT) }}</li>
        <li><strong>Počet cieľov:</strong> {{ targetCount }}</li>
        <li><strong>Zásahy:</strong> {{ hits.length }}</li>
        <li><strong>Falošné poplachy:</strong> {{ falseAlarms.length }}</li>
      </ul>
      <div class="d-flex justify-end">
        <v-btn color="primary" type="submit" class="mr-2" @click="closeTest">Koniec</v-btn>
      </div>
     </v-card>
    </div>
  </div>
</template>

<script>
import axios from '../../axios-auth.js'

export default {
  name: "Mackworth120",
  props: {
    durationSecProp: { type: Number, default: 120}, // kolko to bude trvat
    beaconUrl: { type: String, default: "" },     // voliteľne: endpoint pre navigator.sendBeacon
    participantId: { type: [String, Number], default: "" }, // identifikátor účastníka
  },
  data() {
    return {
      durationSec: this.durationSecProp,
      phase: 'ready', // 'ready' | 'running' | 'finished'
      clockSize: 260,

      // priebeh
      elapsedSec: 0,
      tickTimer: null,
      finalized: false,
      startTime: null,
      myStartTime: 0,
      agreement: 0,

      // hodiny a ciele
      pointerPos: 0,       // 0..59
      pointerAngle: 0,     // deg
      targetSeconds: [],   // plánované sekundy cieľov
      targetCount: 0,
      targetWindowMs: 1000,
      lastTargetAt: null,  // performance.now() pri vzniku cieľa
      targetConsumed: false,
      canHit: false,       // interná logika okna zásahu (bez vizuálnej nápovedy)

      // záznam
      hits: [],            // ms od štartu
      hitsRT: [],          // RT v ms
      falseAlarms: [],     // ms od štartu

      // vizuálna spätná väzba
      handColor: '#333',
      feedbackTimer: null,
    };
  },
  computed: {
    progressPct() {
      return Math.min(100, (this.elapsedSec / this.durationSec) * 100);
    },
    hitRate() {
      return this.targetCount === 0 ? 0 : this.hits.length / this.targetCount;
    },
    falseAlarmsPerMin() {
      return (this.falseAlarms.length / (this.durationSec / 60));
    },
    medianRT() {
      if (!this.hitsRT.length) return null;
      const arr = this.hitsRT.slice().sort((a,b)=>a-b);
      const mid = Math.floor(arr.length/2);
      return arr.length % 2 ? arr[mid] : (arr[mid-1]+arr[mid])/2;
    },
    durationMs() {
      return this.startTime ? Math.max(0, performance.now() - this.startTime) : 0;
    },
    durationSecComputed() {
      return Math.round(this.durationMs / 1000);
    }
  },
  methods: {
    start() {
      this.resetState();
      this.phase = 'running';
      this.planTargets();
      this.startTime = performance.now();
      this.myStartTime = Date.now()
      this.tickTimer = setInterval(this.onTick, 1000);

      // logika pre odchod/unload
      window.addEventListener('beforeunload', this.onBeforeUnload, true);
      window.addEventListener('pagehide', this.onPageHide, true);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    },
    restart() {
      this.cleanupTimersAndListeners();
      this.phase = 'ready';
    },
    resetState() {
      this.elapsedSec = 0;
      this.pointerPos = 0;
      this.pointerAngle = 0;
      this.targetSeconds = [];
      this.targetCount = 0;
      this.canHit = false;
      this.lastTargetAt = null;
      this.targetConsumed = false;
      this.hits = [];
      this.hitsRT = [];
      this.falseAlarms = [];
      this.finalized = false;
      // reset vizuálnej spätnej väzby
      this.handColor = '#333';
      if (this.feedbackTimer) { clearTimeout(this.feedbackTimer); this.feedbackTimer = null; }
    },
    planTargets() {
      // ~10% cieľov, bez „lepenia“ (blok ±2 s)
      const desired = Math.round(this.durationSec * 0.1); // napr. ~12 pri 120 s
      const blocked = new Set();
      const chosen = new Set();
      let guard = 0;
      while (chosen.size < desired && guard < 10000) {
        guard++;
        const s = 2 + Math.floor(Math.random() * (this.durationSec - 4));
        if (blocked.has(s)) continue;
        chosen.add(s);
        for (let b = s - 2; b <= s + 2; b++) blocked.add(b);
      }
      this.targetSeconds = Array.from(chosen).sort((a,b)=>a-b);
      this.targetCount = this.targetSeconds.length;
    },
    onTick() {
      this.elapsedSec++;
      const isTarget = this.targetSeconds.includes(this.elapsedSec);

      if (isTarget) {
        // dvojskok
        this.pointerPos = (this.pointerPos + 2) % 60;
        this.pointerAngle = this.pointerPos * 6;
        this.lastTargetAt = performance.now();
        this.canHit = true;
        this.targetConsumed = false;
        setTimeout(() => { this.canHit = false; }, this.targetWindowMs);
      } else {
        // bežný skok
        this.pointerPos = (this.pointerPos + 1) % 60;
        this.pointerAngle = this.pointerPos * 6;
      }

      if (this.elapsedSec >= this.durationSec) {
        this.finishAndFinalize(true);
      }
    },
    onResponse() {
      if (this.phase !== 'running') return;
      const now = performance.now();
      const tFromStart = now - this.startTime;

      if (this.canHit && !this.targetConsumed) {
        const rt = now - this.lastTargetAt;
        this.hits.push(Math.round(tFromStart));
        this.hitsRT.push(rt);
        this.targetConsumed = true;
        this.canHit = false;

        // správny zásah → zelená
        this.flashHand('#2e7d32', 500);
      } else {
        this.falseAlarms.push(Math.round(tFromStart));

        // falošný poplach → červená
        this.flashHand('#c62828', 500);
      }
    },
    flashHand(color, ms = 500) {
      if (this.feedbackTimer) {
        clearTimeout(this.feedbackTimer);
        this.feedbackTimer = null;
      }
      const prev = '#333';
      this.handColor = color;
      this.feedbackTimer = setTimeout(() => {
        this.handColor = prev;
        this.feedbackTimer = null;
      }, ms);
    },

    finishAndFinalize(completed) {
      if (this.tickTimer) clearInterval(this.tickTimer);
      this.tickTimer = null;
      this.phase = 'finished';
      this.canHit = false;

      const summary = this.buildSummary(completed ? 'completed' : 'abandon');
      this.emitAndBeacon(summary);
      this.finalized = true;

      this.cleanupListenersOnly();
    },

    buildSummary(endReason) {
      const duration_sec = this.durationSecComputed; // skutočné trvanie
      this.writeAnswer(endReason)
      return {
        participant_id: this.participantId || null,
        end_reason: endReason,                  // 'completed' | 'abandon'
        duration_sec: duration_sec,             // koľko reálne vydržal
        planned_duration_sec: this.durationSec, // plán
        target_count: this.targetCount,
        hits: this.hits.length,
        false_alarms: this.falseAlarms.length,
        hit_rate: this.targetCount ? (this.hits.length / this.targetCount) : 0,
        fa_per_min: this.falseAlarms.length / (this.durationSec / 60),
        median_rt_ms: this.medianRT === null ? null : Math.round(this.medianRT),
        timestamps: {
          hits_ms: this.hits.slice(),
          false_ms: this.falseAlarms.slice(),
          targets_sec: this.targetSeconds.slice()
        },
        client_time_iso: new Date().toISOString()
      };
    },
    emitAndBeacon(summary) {
      // emit pre rodiča
      this.$emit('finalize', summary);
    },
    onBeforeUnload() {
      if (this.phase === 'running' && !this.finalized) {
        this.finishAndFinalize(false);
      }
    },
    onPageHide() {
      if (this.phase === 'running' && !this.finalized) {
        this.finishAndFinalize(false);
      }
    },
    onVisibilityChange() {
      // nič: len by sa dalo logovať, ak by ste chceli
    },
    cleanupTimersAndListeners() {
      if (this.tickTimer) clearInterval(this.tickTimer);
      this.tickTimer = null;
      this.cleanupListenersOnly();
      if (this.feedbackTimer) { clearTimeout(this.feedbackTimer); this.feedbackTimer = null; }
    },
    cleanupListenersOnly() {
      window.removeEventListener('beforeunload', this.onBeforeUnload, true);
      window.removeEventListener('pagehide', this.onPageHide, true);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
    },
    writeAnswer(_final) {
    const _timestamps = {
          hits_ms: this.hits.slice(),
          false_ms: this.falseAlarms.slice(),
          targets_sec: this.targetSeconds.slice()
        }
        const _score = (this.targetCount > 0 ? (this.hits.length / this.targetCount) : 0 ) * (1000/ (this.medianRT === null ? 1000 : Math.round(this.medianRT)))
    const _data = {
          id: 0,
          test_id: this.$route.params.id,
          score: _score,
          answers : JSON.stringify(_timestamps),
          final: _final = 'completed' ? 1 : 0,
          rslt: '' + this.hits.length + ' / ' + this.targetCount + ', ' + (this.medianRT>0 ? this.medianRT+ ' ms': '') ,
          agreement: this.agreement,
          time_length: this.myStartTime ? Math.round((Date.now() - this.myStartTime)/1000) : 0
      } 
      axios.post('/write-my-person-test-answer', _data, this.$store.getters.getAxiosConfig)
      .then(res => {
          // this.answer_id = res.data.answer_id
      })
      .catch(error => {
          this.$store.commit('setFeedback', error)
      })
    },
    closeTest() {
      this.$router.push({name:'personalisation_tests', params: {}}) 
    },
  },
  beforeUnmount() {
    this.finishAndFinalize()
    this.cleanupTimersAndListeners();
  },

};
</script>

<style scoped>
/* jednoduché utility (ak nepoužívate Tailwind) */
.max-w-xl { max-width: 36rem; }
.p-4 { padding: 1rem; }
.mx-auto { margin-left: auto; margin-right: auto; }
.mb-2 { margin-bottom: .5rem; }
.mb-3 { margin-bottom: .75rem; }
.my-4 { margin-top: 1rem; margin-bottom: 1rem; }
.mt-1 { margin-top: .25rem; }
.mt-4 { margin-top: 1rem; }
.text-center { text-align: center; }
.text-xl { font-size: 1.25rem; line-height: 1.75rem; }
.font-semibold { font-weight: 600; }
.flex { display: flex; }
.items-center { align-items: center; }
.justify-center { justify-content: center; }
.justify-between { justify-content: space-between; }
.text-xs { font-size: .75rem; }
.text-sm { font-size: .875rem; }
.bg-gray-50 { background-color: #f9fafb; }
.cursor-pointer { cursor: pointer; }
.overflow-auto { overflow: auto; }
.select-none { user-select: none; }
</style>
